import { usePrimaryColor } from '../../branding/usePrimaryColor';

interface ConversionFunnelProps {
  leads: number;
  booked: number;
  completed: number;
  paid: number;
  medianTimeToBookHours: number | null;
  loading?: boolean;
  periodLabel?: string;
}

const STAGES = ['Leads', 'Booked', 'Completed', 'Paid'] as const;
const OPACITY = [1.0, 0.75, 0.5, 0.35] as const;

function formatMedianTime(hours: number): string {
  const totalMinutes = Math.round(hours * 60);

  if (totalMinutes < 1) return '<1m';
  if (totalMinutes < 60) return `${totalMinutes}m`;
  if (totalMinutes < 24 * 60) {
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  }
  const totalHours = Math.round(hours);
  const d = Math.floor(totalHours / 24);
  const h = totalHours % 24;
  return h > 0 ? `${d}d ${h}h` : `${d}d`;
}

export function ConversionFunnel({
  leads,
  booked,
  completed,
  paid,
  medianTimeToBookHours,
  loading = false,
  periodLabel,
}: ConversionFunnelProps) {
  const primaryColor = usePrimaryColor();
  const counts = [leads, booked, completed, paid];

  // Loading state
  if (loading && leads === 0 && booked === 0) {
    return (
      <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
        <div className="px-4 py-3 hair-b">
          <div className="h-4 w-40 bg-canvas animate-pulse rounded" />
        </div>
        <div className="px-4 pt-4 pb-3 space-y-3">
          {[1, 0.75, 0.5, 0.35].map((_, i) => (
            <div key={i} className="space-y-1">
              <div className="h-3 w-16 bg-canvas animate-pulse rounded" />
              <div
                className="h-7 bg-canvas animate-pulse rounded"
                style={{ width: `${100 - i * 20}%` }}
              />
            </div>
          ))}
          <div className="pt-2">
            <div className="h-3 w-32 bg-canvas animate-pulse rounded" />
          </div>
        </div>
      </div>
    );
  }

  // Empty state
  if (leads === 0 && !loading) {
    return (
      <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
        <div className="px-4 py-3 hair-b flex items-center gap-2">
          <h3 className="text-[14px] font-semibold">Conversion funnel</h3>
          {periodLabel && (
            <span className="text-[11.5px] text-ink-4">· {periodLabel.toLowerCase()}</span>
          )}
        </div>
        <div className="px-4 pt-4 pb-3">
          <div className="text-center py-8">
            <p className="text-[11.5px] text-ink-4">
              Leads will appear here as they come in through your booking pages.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const maxCount = leads || 1;

  return (
    <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 hair-b flex items-center gap-2">
        <h3 className="text-[14px] font-semibold">Conversion funnel</h3>
        {periodLabel && (
          <span className="text-[11.5px] text-ink-4">· {periodLabel.toLowerCase()}</span>
        )}
      </div>

      {/* Funnel bars */}
      <div className="px-4 pt-4 pb-3">
        <div className="space-y-2">
          {STAGES.map((label, i) => {
            const count = counts[i];
            const widthPct = Math.max((count / maxCount) * 100, count > 0 ? 4 : 0);
            const prevCount = i > 0 ? counts[i - 1] : null;
            const dropOff =
              prevCount !== null && prevCount > 0
                ? Math.round(((prevCount - count) / prevCount) * 100)
                : null;

            return (
              <div key={label}>
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-[11.5px] text-ink-3">{label}</span>
                  {dropOff !== null && dropOff > 0 && (
                    <span className="text-[10px] text-ink-4">
                      -{dropOff}%
                    </span>
                  )}
                </div>
                <div className="relative h-7 w-full rounded overflow-hidden bg-canvas">
                  <div
                    className="h-full rounded transition-all duration-300"
                    style={{
                      width: `${widthPct}%`,
                      backgroundColor: primaryColor,
                      opacity: OPACITY[i],
                    }}
                  />
                  <span
                    className="absolute inset-y-0 flex items-center text-[12px] font-medium num"
                    style={{
                      left: widthPct > 20 ? '8px' : `calc(${widthPct}% + 6px)`,
                      color: widthPct > 20 ? '#fff' : 'var(--color-ink-2)',
                    }}
                  >
                    {count.toLocaleString()}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Median time-to-book */}
        {medianTimeToBookHours !== null && (
          <div className="mt-4 pt-3 hair-t flex items-baseline gap-2">
            <span className="text-[11.5px] text-ink-4">Median time to book</span>
            <span className="font-display text-[16px] font-medium num">
              {formatMedianTime(medianTimeToBookHours)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
